import type { types } from '@bedc/types';

import { slopedSegmentLengths } from './lengths';
import { packSticksDetailed } from './pack';
import { projectLineSegmentsFromArea } from './project';
import type { PackCollector, PackMaterial, ProjectionCollector, ProjectionLayer } from './types';

/**
 * Drawing-unit noise floor for degenerate grazes. An engine constant — the
 * real sliver rule is the resolution inside `segmentLengths`.
 */
const grazeFloor = 1;

/**
 * THE PAVED ROAD: the single call a recipe admin writes.
 *
 *   packProjectedLineSegments(parameter.spacing[, angle[, maxCuts]])
 *
 * The arguments are exactly the author's DECISIONS: spacing in real units,
 * angle and max-cuts optional. Everything else is engine-bound through this
 * factory — scale and measurement system, and equally the recipe's assigned
 * layers and its selected materials, which the formula field cannot choose and
 * so has no business naming. That is why the produced function lives on the
 * SCOPE side of the evaluator, beside other context-bound bindings —
 * context-bound functions cannot be `parser.functions` registrations.
 *
 * ! Binding layers and materials HERE, rather than accepting them as
 * arguments, is what keeps them out of formula scope entirely: an expression
 * like `layers+materials` cannot be written, let alone saved.
 *
 * Internally it IS the composition — project → lengths → pack — pinned by an
 * equivalence test in `studProjectionFormula.spec.ts` so the paved road and
 * the Tier-1 primitives cannot drift.
 *
 * `onProject` observes each layer's computed geometry (for the derived
 * "projected lines" layer) without touching the returned quantities. That
 * geometry is always PLAN; a layer's `slopeFactor` reaches the lengths only.
 * `onPack` observes the cut list and sticks the same way, for the breakdown.
 */
export const makePackProjectedLineSegments =
	(
		scale: number,
		measurementSystem: types.ui.MeasurementSystem,
		layers: ProjectionLayer[],
		materials: PackMaterial[],
		onProject?: ProjectionCollector,
		onPack?: PackCollector
	) =>
	(spacing: number, angleDegree = 90, maxCutsPerStick = 0): number[] => {
		const projections = layers.map((layer, layerIndex) => {
			const projection = projectLineSegmentsFromArea(
				layer.paths,
				layer.cutouts ?? [],
				spacing / scale,
				angleDegree,
				grazeFloor
			);

			onProject?.({ layer, layerIndex, projection, spacing: spacing / scale, angleDegree });

			return projection;
		});

		const pieces = slopedSegmentLengths(
			projections.map((projection, layerIndex) => ({
				projection,
				slopeFactor: layers[layerIndex]?.slopeFactor,
			})),
			scale,
			measurementSystem
		);
		const { quantities, sticks } = packSticksDetailed(pieces, materials, maxCutsPerStick);

		onPack?.({ spacing, angleDegree, maxCutsPerStick, pieces, sticks, quantities });

		return quantities;
	};
